import React from "react";
import { motion } from "framer-motion";
import { CardSVG } from "../cards";
import { Card } from "../types/game";

interface DrawDeckProps {
  deckCount: number;
  onClick: () => void;
  isMyTurn: boolean;
  isMobile?: boolean;
  disabled?: boolean;
  pulseIntensity?: "normal" | "strong"; // Pulse intensity for visual priority
}

/**
 * DrawDeck Component
 * 
 * Visual representation of the draw deck.
 * - Smaller than discard pile
 * - Positioned near discard pile
 * - Clickable/tappable
 * - Shows card count (if available)
 * - Pulsing animation when it's player's turn
 */
export const DrawDeck: React.FC<DrawDeckProps> = ({
  deckCount,
  onClick,
  isMyTurn,
  isMobile = false,
  disabled = false,
  pulseIntensity = "normal",
}) => {
  // Use a placeholder card (back of card) for visual representation
  // In a real implementation, you might have a CardBack component
  const placeholderCard: Card = {
    color: "wild" as any,
    value: "wild" as any,
  };

  const scale = isMobile ? 0.22 : 0.26; // Smaller than discard pile for better visibility
  const cardWidth = 300 * scale;
  const cardHeight = 420 * scale;

  return (
    <motion.div
      onClick={disabled ? undefined : onClick}
      whileHover={!disabled && !isMobile ? { scale: 1.05 } : {}}
      whileTap={!disabled ? { scale: 0.95 } : {}}
      animate={{
        scale: isMyTurn && !disabled 
          ? pulseIntensity === "strong" 
            ? [1, 1.1, 1] // Stronger pulse for forced draw
            : [1, 1.05, 1] // Normal pulse
          : 1,
      }}
      transition={{
        scale: {
          duration: isMyTurn ? (pulseIntensity === "strong" ? 1.0 : 1.5) : 0.2,
          repeat: isMyTurn ? Infinity : 0,
          ease: "easeInOut",
        },
      }}
      style={{
        position: "relative",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        filter: disabled ? "grayscale(0.5)" : "none",
        zIndex: 2,
      }}
    >
      {/* Card Stack Visual */}
      <div
        style={{
          position: "relative",
          width: `${cardWidth}px`,
          height: `${cardHeight}px`,
        }}
      >
        {/* Back card (main visual) - Crown symbol design */}
        <div
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #1E88E5 0%, #1565C0 50%, #0D47A1 100%)",
            border: "2px solid rgba(255,255,255,0.3)",
            boxShadow: `
              0 8px 24px rgba(0,0,0,0.4),
              0 4px 8px rgba(0,0,0,0.3),
              inset 0 1px 0 rgba(255,255,255,0.1)
            `,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          {/* Crown symbol with smiling face */}
          <div
            style={{
              fontSize: isMobile ? "32px" : "40px",
              fontWeight: "900",
              color: "#FFD700",
              textShadow: "0 2px 8px rgba(0,0,0,0.5), 0 0 12px rgba(255, 215, 0, 0.5)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "2px",
            }}
          >
            <span>👑</span>
            <span style={{ fontSize: isMobile ? "16px" : "20px" }}>😊</span>
          </div>
        </div>

        {/* Stacked card effect (depth) */}
        <div
          style={{
            position: "absolute",
            top: "3px",
            left: "3px",
            width: "100%",
            height: "100%",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #34495e 0%, #2c3e50 100%)",
            border: "2px solid rgba(255,255,255,0.15)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            zIndex: 1,
            opacity: 0.7,
          }}
        />

        {/* Glow effect when it's player's turn - enhanced for forced draw */}
        {isMyTurn && !disabled && (
          <motion.div
            animate={{
              opacity: pulseIntensity === "strong" 
                ? [0.6, 0.9, 0.6] // Stronger glow for forced draw
                : [0.4, 0.6, 0.4], // Normal glow
              scale: pulseIntensity === "strong"
                ? [1, 1.2, 1] // Larger glow for forced draw
                : [1, 1.1, 1],
            }}
            transition={{
              duration: pulseIntensity === "strong" ? 1.0 : 1.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              position: "absolute",
              inset: pulseIntensity === "strong" ? "-12px" : "-8px",
              borderRadius: "50%",
              background: pulseIntensity === "strong"
                ? "radial-gradient(circle, rgba(102, 126, 234, 0.6) 0%, transparent 70%)"
                : "radial-gradient(circle, rgba(102, 126, 234, 0.4) 0%, transparent 70%)",
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
        )}
      </div>

      {/* Card Count Badge (if deck count is known) */}
      {deckCount > 0 && (
        <div
          style={{
            position: "absolute",
            top: "-6px", // Reduced from -8px
            right: "-6px", // Reduced from -8px
            minWidth: isMobile ? "20px" : "24px", // Reduced from 24px/28px
            height: isMobile ? "20px" : "24px", // Reduced from 24px/28px
            borderRadius: "50%",
            background: "rgba(0,0,0,0.8)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: isMobile ? "9px" : "11px", // Reduced from 10px/12px
            fontWeight: "700",
            boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
            border: "2px solid rgba(255,255,255,0.3)",
            zIndex: 3,
          }}
        >
          {deckCount}
        </div>
      )}
    </motion.div>
  );
};
